<template>
  <div class="employee-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container mt-page">
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">My Training</h1>
              <p class="dashboard-subtitle">Apply for courses, track approvals, and manage reports</p>
            </div>
            <div class="header-actions">
              <button class="mt-btn ghost" @click="loadData" :disabled="loading">
                <i class="bx bx-refresh" :class="{ 'spin': loading }"></i> Refresh
              </button>
            </div>
          </div>
        </div>

        <div class="metrics-grid">
          <div class="metric-card gradient-1">
            <div class="metric-icon"><i class="bx bx-send"></i></div>
            <div class="metric-content">
              <p class="metric-label">Applications</p>
              <h2 class="metric-value">{{ applications.length }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-2">
            <div class="metric-icon"><i class="bx bx-book"></i></div>
            <div class="metric-content">
              <p class="metric-label">Scheduled</p>
              <h2 class="metric-value">{{ scheduledTrainings.length }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-3">
            <div class="metric-icon"><i class="bx bx-file-blank"></i></div>
            <div class="metric-content">
              <p class="metric-label">Reports filed</p>
              <h2 class="metric-value">{{ myReports.length }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-4">
            <div class="metric-icon"><i class="bx bx-certification"></i></div>
            <div class="metric-content">
              <p class="metric-label">Certifications</p>
              <h2 class="metric-value">{{ certifications.length }}</h2>
            </div>
          </div>
        </div>

        <div v-if="notices.length" class="mt-notice-strip">
          <div class="mt-notice-head"><i class="bx bx-bell"></i> Notifications</div>
          <ul>
            <li v-for="n in notices.slice(0, 6)" :key="n.id">
              <time>{{ n.created_at }}</time>
              <span>{{ n.message }}</span>
            </li>
          </ul>
        </div>

        <div class="mt-actions-bar">
          <button type="button" class="mt-btn primary" @click="openApplicationForm">
            <i class="bx bx-edit-alt"></i> Apply for training
          </button>
          <button type="button" class="mt-btn" @click="openReportForm">
            <i class="bx bx-file"></i> Training report
          </button>
        </div>

        <div v-show="showAppForm" ref="appFormPanel" class="mt-panel">
          <div class="mt-panel-head">
            <div>
              <h2>Training application</h2>
            </div>
            <button type="button" class="mt-btn ghost" @click="showAppForm = false">Close</button>
          </div>

          <div v-if="prefillLoading" class="mt-loading">Loading your details…</div>
          <div v-else-if="!hasPrefill" class="mt-alert warn">
            Employee register record not found for your login. Contact HR to link your P/No.
          </div>

          <form v-else class="mt-form" @submit.prevent="submitApplication(true)">
            <section class="mt-section">
              <h3>Your details <span class="mt-pill">From register</span></h3>
              <div class="mt-bio-grid">
                <div class="mt-bio"><label>1. Name</label><div>{{ appForm.full_name || '—' }}</div></div>
                <div class="mt-bio"><label>2. P/NO</label><div>{{ appForm.emp_no || '—' }}</div></div>
                <div class="mt-bio"><label>ID NO</label><div>{{ appForm.national_id || '—' }}</div></div>
                <div class="mt-bio"><label>3. Designation</label><div>{{ appForm.designation || '—' }}</div></div>
                <div class="mt-bio"><label>Job Group</label><div>{{ appForm.job_group || '—' }}</div></div>
                <div class="mt-bio"><label>4. Department</label><div>{{ appForm.department_name || '—' }}</div></div>
                <div class="mt-bio"><label>Section</label><div>{{ appForm.section_name || '—' }}</div></div>
                <div class="mt-bio"><label>Station</label><div>{{ appForm.station || '—' }}</div></div>
                <div class="mt-bio"><label>Sub County</label><div>{{ appForm.sub_county || '—' }}</div></div>
                <div class="mt-bio span2"><label>5. Address</label><div>{{ appForm.address_text || '—' }}</div></div>
                <div class="mt-bio"><label>Phone No</label><div>{{ appForm.phone || '—' }}</div></div>
                <div class="mt-bio"><label>Email</label><div>{{ appForm.email || '—' }}</div></div>
                <div class="mt-bio"><label>6. Date of Birth</label><div>{{ formatDate(appForm.date_of_birth) }}</div></div>
                <div class="mt-bio"><label>7. Gender</label><div>{{ appForm.gender || '—' }}</div></div>
                <div class="mt-bio"><label>8. Disability (if any)</label><div>{{ appForm.disability || 'None recorded' }}</div></div>
                <div class="mt-bio"><label>9. Date of 1st Appointment</label><div>{{ formatDate(appForm.date_first_appointment) }}</div></div>
              </div>
            </section>

            <section class="mt-section">
              <h3>Previous course &amp; bond</h3>
              <div class="mt-fields">
                <label class="span2"><span>10. Title of last course attended</span>
                  <input v-model="appForm.last_course_title" type="text"></label>
                <label><span>11. Last course from</span>
                  <input v-model="appForm.last_course_from" type="date"></label>
                <label><span>Last course to</span>
                  <input v-model="appForm.last_course_to" type="date"></label>
                <label><span>Bond period</span>
                  <input v-model="appForm.last_bond_period" type="text"></label>
                <label><span>Bond amount (Ksh)</span>
                  <input v-model="appForm.last_bond_amount" type="number" step="0.01" min="0"></label>
                <label><span>Date bond expired</span>
                  <input v-model="appForm.last_bond_expired" type="date"></label>
              </div>
            </section>

            <section class="mt-section accent">
              <h3>Course requested</h3>
              <div class="mt-fields">
                <label class="span2"><span>12. Title of course requested <em>*</em></span>
                  <input v-model="appForm.course_title" type="text" required placeholder="Full name of the course"></label>
                <label><span>13. Duration from</span>
                  <input v-model="appForm.course_from" type="date"></label>
                <label><span>Duration to</span>
                  <input v-model="appForm.course_to" type="date"></label>
                <label><span>14. Mode of study</span>
                  <select v-model="appForm.mode_of_study">
                    <option value="">Select…</option>
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Online / distance</option>
                    <option>Block release</option>
                    <option>Sandwich</option>
                  </select>
                </label>
                <label><span>15. Venue</span>
                  <input v-model="appForm.venue" type="text" placeholder="Institution / location"></label>
                <label><span>16. Sponsor</span>
                  <input v-model="appForm.sponsor" type="text" placeholder="Self, County, partner…"></label>
                <label><span>17. Total training costs (Kshs)</span>
                  <input v-model="appForm.total_cost" type="number" step="0.01" min="0"></label>
                <label class="span2"><span>18. Applicant signature (type your full name)</span>
                  <input v-model="appForm.applicant_signature" type="text"></label>
              </div>
            </section>

            <p v-if="appMsg" class="mt-msg" :class="appOk ? 'ok' : 'err'">{{ appMsg }}</p>
            <div class="mt-form-actions">
              <button type="button" class="mt-btn" :disabled="appSubmitting" @click="submitApplication(false)">
                {{ appSubmitting ? 'Saving…' : 'Save draft' }}
              </button>
              <button type="submit" class="mt-btn primary" :disabled="appSubmitting">
                {{ appSubmitting ? 'Submitting…' : 'Submit application' }}
              </button>
            </div>
          </form>
        </div>

        <div class="mt-panel">
          <div class="mt-panel-head"><h2>My applications</h2></div>
          <div v-if="applications.length === 0" class="mt-empty">
            No applications yet. Click <strong>Apply for training</strong> to start.
          </div>
          <div v-else class="mt-table-wrap">
            <table class="mt-table">
              <thead>
                <tr>
                  <th>Course</th><th>Dates</th><th>Term</th><th>Stage</th><th>Status</th><th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in applications" :key="a.id">
                  <td><strong>{{ a.course_title }}</strong></td>
                  <td>{{ formatDate(a.course_from) }} → {{ formatDate(a.course_to) }}</td>
                  <td>{{ a.training_term === 'long_term' ? 'Long-term' : (a.training_term === 'short_term' ? 'Short-term' : '—') }}</td>
                  <td><span class="mt-stage">{{ stageLabel(a.stage) }}</span></td>
                  <td><span class="mt-status" :class="String(a.status || '')">{{ a.status }}</span></td>
                  <td>{{ a.updated_at || a.created_at }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

<div v-show="showReportForm" ref="reportFormPanel" class="mt-panel">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-file"></i> Training report form</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">
              Complete the county <strong>Training Report Form</strong>. You may submit for <strong>training you already attended</strong> (retrospective) or recent activity—use the actual dates you were in session.
            </p>
            <p class="hr-mb-3">
              <a class="hr-link-pdf" :href="formPdfUrl" target="_blank" rel="noopener">
                <i class="bx bx-download"></i> Download blank form (PDF)
              </a>
            </p>
            <label class="tr-field tr-span-2 tr-check-row">
              <span><input v-model="reportForm.is_past_training" type="checkbox" /> This report is for training I have already completed (retrospective)</span>
            </label>
            <div v-if="targetsError" class="hr-alert hr-alert-warning">{{ targetsError }}</div>
            <form class="tr-form" @submit.prevent="submitTrainingReport">
              <div class="tr-grid">
                <label class="tr-field tr-span-2">
                  <span>To be submitted to <span class="req">*</span></span>
                  <select v-model.number="reportForm.submit_target_id" class="tr-input" required>
                    <option :value="0" disabled>Select recipient</option>
                    <option v-for="t in submitTargets" :key="t.id" :value="t.id">{{ t.label }}</option>
                  </select>
                </label>
                <label class="tr-field">
                  <span>Officer name</span>
                  <input v-model="reportForm.officer_name" type="text" class="tr-input" placeholder="Auto-filled from profile if empty">
                </label>
                <label class="tr-field">
                  <span>Designation</span>
                  <input v-model="reportForm.designation" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Department / section</span>
                  <input v-model="reportForm.department_section" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Station</span>
                  <input v-model="reportForm.station" type="text" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Title of training / workshop / seminar <span class="req">*</span></span>
                  <input v-model="reportForm.training_title" type="text" class="tr-input" required>
                </label>
                <label class="tr-field">
                  <span>Training institution / organizer</span>
                  <input v-model="reportForm.institution_organizer" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Venue</span>
                  <input v-model="reportForm.venue" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Duration from</span>
                  <input v-model="reportForm.date_from" type="date" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Duration to</span>
                  <input v-model="reportForm.date_to" type="date" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Sponsoring agency (if any)</span>
                  <input v-model="reportForm.sponsoring_agency" type="text" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Type of training</span>
                  <input v-model="reportForm.training_type" type="text" class="tr-input" placeholder="e.g. Local · Workshop">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Objectives of the training</span>
                  <textarea v-model="reportForm.objectives" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Summary of training content</span>
                  <textarea v-model="reportForm.summary_content" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Key learnings and skills acquired</span>
                  <textarea v-model="reportForm.key_learnings" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Application / relevance to work</span>
                  <textarea v-model="reportForm.application_relevance" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Challenges / lessons learned</span>
                  <textarea v-model="reportForm.challenges" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Recommendations</span>
                  <textarea v-model="reportForm.recommendations" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Attachments (describe what you will attach / have attached)</span>
                  <input v-model="reportForm.attachments_note" type="text" class="tr-input" placeholder="Invitation, certificate, materials…">
                </label>
                <label class="tr-field">
                  <span><input type="checkbox" v-model="reportForm.attachment_invitation"> Copy of Training Invitation / Nomination Letter</span>
                </label>
                <label class="tr-field">
                  <span><input type="checkbox" v-model="reportForm.attachment_certificate"> Copy of Certificate of Participation / Completion</span>
                </label>
                <label class="tr-field tr-span-2">
                  <span><input type="checkbox" v-model="reportForm.attachment_materials"> Training Materials / Program attached</span>
                </label>

                <div class="tr-section-title tr-span-2">Supporting uploads (optional)</div>
                <p class="hr-text-muted tr-span-2 hr-mb-1" style="grid-column: span 2">
                  PDF or images up to 10 MB each. HR can view these from <strong>Training Management</strong>.
                </p>
                <label class="tr-field tr-span-2">
                  <span>Invitation / nomination letter</span>
                  <input ref="docInvitation" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>
                <label class="tr-field tr-span-2">
                  <span>Certificate of participation</span>
                  <input ref="docCertificate" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>
                <label class="tr-field tr-span-2">
                  <span>Training materials / program</span>
                  <input ref="docMaterials" type="file" class="tr-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>

                <div class="tr-section-title tr-span-2">J. Officer's Declaration</div>
                <label class="tr-field">
                  <span>Officer signature/name</span>
                  <input v-model="reportForm.officer_signature" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Date</span>
                  <input v-model="reportForm.officer_declaration_date" type="date" class="tr-input">
                </label>

                <div class="tr-section-title tr-span-2">K. Supervisor's Comments</div>
                <label class="tr-field tr-span-2">
                  <span>Comments on relevance of training to duties/performance</span>
                  <textarea v-model="reportForm.supervisor_comments" class="tr-textarea" rows="3"></textarea>
                </label>
                <label class="tr-field">
                  <span>Name of supervisor</span>
                  <input v-model="reportForm.supervisor_name" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor designation</span>
                  <input v-model="reportForm.supervisor_designation" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor signature</span>
                  <input v-model="reportForm.supervisor_signature" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Supervisor date</span>
                  <input v-model="reportForm.supervisor_date" type="date" class="tr-input">
                </label>

                <div class="tr-section-title tr-span-2">L. Directorate of HRM & Development (Official Use)</div>
                <label class="tr-field">
                  <span>Received by</span>
                  <input v-model="reportForm.hr_received_by" type="text" class="tr-input">
                </label>
                <label class="tr-field">
                  <span>Date received</span>
                  <input v-model="reportForm.hr_received_date" type="date" class="tr-input">
                </label>
                <label class="tr-field tr-span-2">
                  <span>Remarks</span>
                  <textarea v-model="reportForm.hr_remarks" class="tr-textarea" rows="2"></textarea>
                </label>
                <label class="tr-field tr-span-2">
                  <span>Action taken</span>
                  <textarea v-model="reportForm.hr_action_taken" class="tr-textarea" rows="2"></textarea>
                </label>
              </div>
              <p v-if="reportMsg" class="hr-mt-2" :class="reportOk ? 'hr-text-success' : 'hr-text-danger'">{{ reportMsg }}</p>
              <button type="submit" class="refresh-btn hr-mt-2" :disabled="reportSubmitting">
                {{ reportSubmitting ? "Submitting…" : "Submit training report" }}
              </button>
            </form>
          </div>
        </div>

        <div class="mt-panel">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> My submitted training reports</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="myReports.length === 0" class="hr-text-center hr-py-4 hr-text-muted">No training reports submitted yet.</div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Training title</th>
                    <th>Submitted to</th>
                    <th>Dates</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in myReports" :key="r.id">
                    <td>{{ r.created_at || "—" }}</td>
                    <td>{{ r.training_title }}</td>
                    <td>{{ r.submit_to_label }}</td>
                    <td>{{ r.date_from || "—" }} → {{ r.date_to || "—" }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ r.status }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="mt-panel">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-certification"></i> My Certifications</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Certificate / Training</th>
                    <th>Issuing body</th>
                    <th>Issue date</th>
                    <th>Expiry</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, idx) in certifications" :key="c.certificateID">
                    <td>{{ idx + 1 }}</td>
                    <td>
                      {{ c.certificateName }}
                      <span v-if="c.certificateNumber" class="hr-text-muted">({{ c.certificateNumber }})</span>
                      <span v-if="c.trainingName" class="hr-small hr-d-block hr-text-muted">Training: {{ c.trainingName }}</span>
                    </td>
                    <td>{{ c.issuingBody || '—' }}</td>
                    <td>{{ c.issueDate }}</td>
                    <td>{{ c.expiryDate || '—' }}</td>
                    <td><span class="hr-badge" :class="statusClass(c.verificationStatus)">{{ c.verificationStatus }}</span></td>
                  </tr>
                  <tr v-if="certifications.length === 0">
                    <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No certifications recorded for you yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="mt-panel">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-event"></i> My scheduled trainings</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">Trainings for you or your department. View details and confirm attendance.</p>
            <div v-if="scheduledTrainings.length === 0" class="hr-text-center hr-py-4 hr-text-muted">No trainings scheduled for you.</div>
            <div v-else class="training-list">
              <div v-for="s in scheduledTrainings" :key="s.schedule_id" class="training-item">
                <div class="training-item-icon"><i class="bx bx-book"></i></div>
                <div class="training-item-body">
                  <strong>{{ s.trainingName }}</strong>
                  <span class="hr-badge hr-badge-info">{{ s.trainingType }}</span>
                  <span v-if="s.acknowledged" class="hr-badge hr-badge-success hr-ml-2">Confirmed</span>
                  <p class="hr-text-muted hr-mb-1 hr-small">
                    {{ s.start_date }}<span v-if="s.end_date && s.end_date !== s.start_date"> – {{ s.end_date }}</span>
                    · {{ s.venue || 'Venue TBC' }}
                  </p>
                  <p class="hr-text-muted hr-mb-2 hr-small">Audience: {{ s.audience_label || '—' }}</p>
                  <div class="training-item-actions">
                    <button type="button" class="refresh-btn sm" @click="openScheduleDetail(s)">View details</button>
                    <button
                      v-if="!s.acknowledged"
                      type="button"
                      class="refresh-btn sm primary"
                      :disabled="ackSubmitting === s.schedule_id"
                      @click="acknowledgeSchedule(s)"
                    >
                      {{ ackSubmitting === s.schedule_id ? 'Saving…' : 'I will attend' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="detailSchedule" class="hr-modal-overlay" @click.self="detailSchedule = null">
          <div class="hr-modal detail-modal">
            <div class="hr-modal-header">
              <h3>{{ detailSchedule.trainingName }}</h3>
              <button type="button" class="hr-modal-close" @click="detailSchedule = null">&times;</button>
            </div>
            <div class="hr-p-3 detail-body">
              <p><strong>Code:</strong> {{ detailSchedule.trainingCode || '—' }}</p>
              <p><strong>Dates:</strong> {{ detailSchedule.start_date }}<span v-if="detailSchedule.end_date && detailSchedule.end_date !== detailSchedule.start_date"> – {{ detailSchedule.end_date }}</span></p>
              <p><strong>Venue:</strong> {{ detailSchedule.venue || '—' }}</p>
              <p><strong>Provider:</strong> {{ detailSchedule.provider || '—' }}</p>
              <p><strong>Type:</strong> {{ detailSchedule.trainingType || '—' }}</p>
              <p><strong>Audience:</strong> {{ detailSchedule.audience_label || '—' }}</p>
              <p v-if="detailSchedule.duration_days || detailSchedule.duration_hours"><strong>Duration:</strong> {{ detailSchedule.duration_days ? detailSchedule.duration_days + ' days' : '' }} {{ detailSchedule.duration_hours ? detailSchedule.duration_hours + ' hours' : '' }}</p>
              <p v-if="detailSchedule.description"><strong>Description:</strong> {{ detailSchedule.description }}</p>
              <p v-if="detailSchedule.learning_objectives"><strong>Objectives:</strong> {{ detailSchedule.learning_objectives }}</p>
              <p v-if="detailSchedule.prerequisites"><strong>Prerequisites:</strong> {{ detailSchedule.prerequisites }}</p>
              <p v-if="detailSchedule.notes"><strong>Session notes:</strong> {{ detailSchedule.notes }}</p>
              <p v-if="detailSchedule.acknowledged_at" class="hr-text-success"><strong>Attendance confirmed:</strong> {{ detailSchedule.acknowledged_at }}</p>
              <p v-if="ackMsg" :class="ackOk ? 'hr-text-success' : 'hr-text-danger'">{{ ackMsg }}</p>
              <button
                v-if="!detailSchedule.acknowledged"
                type="button"
                class="refresh-btn primary hr-mt-2"
                :disabled="ackSubmitting === detailSchedule.schedule_id"
                @click="acknowledgeSchedule(detailSchedule)"
              >
                {{ ackSubmitting === detailSchedule.schedule_id ? 'Saving…' : 'I confirm I will attend' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import { authUrl, execute } from "@/api";

const STAGE_LABELS = {
  draft: "Draft",
  sub_county_review: "Sub-county HR Committee",
  sub_county_docs: "Sub-county HR — minutes/report",
  departmental_review: "Departmental HR Committee",
  departmental_docs: "Departmental HR — minutes/report",
  chrmac_review: "CHRMAC",
  chrmac_docs: "Head of Training package",
  psb_ceo_review: "PSB CEO review",
  bonding_upload: "Awaiting bonding letter",
  approved: "Approved — proceed to training",
  rejected: "Rejected",
  in_training: "In training",
  completed: "Completed",
};

function emptyAppForm() {
  return {
    id: 0,
    full_name: "",
    emp_no: "",
    national_id: "",
    designation: "",
    job_group: "",
    department_name: "",
    section_name: "",
    station: "",
    sub_county: "",
    address_text: "",
    phone: "",
    email: "",
    date_of_birth: "",
    gender: "",
    disability: "",
    date_first_appointment: "",
    last_course_title: "",
    last_course_from: "",
    last_course_to: "",
    last_bond_period: "",
    last_bond_amount: "",
    last_bond_expired: "",
    course_title: "",
    course_from: "",
    course_to: "",
    mode_of_study: "",
    venue: "",
    sponsor: "",
    total_cost: "",
    applicant_signature: "",
    supervisor_justification: "",
    supervisor_name: "",
    supervisor_designation: "",
    supervisor_signed_at: "",
    authorized_officer_justification: "",
    authorized_officer_name: "",
    authorized_officer_designation: "",
    authorized_officer_signed_at: "",
    is_health_path: 0,
  };
}

export default {
  name: "MyTraining",
  components: { Navigation },
  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      certifications: [],
      scheduledTrainings: [],
      detailSchedule: null,
      ackSubmitting: 0,
      ackMsg: "",
      ackOk: false,
      submitTargets: [],
      targetsError: "",
      myReports: [],
      showReportForm: false,
      showAppForm: false,
      prefillLoading: false,
      hasPrefill: false,
      applications: [],
      notices: [],
      appSubmitting: false,
      appMsg: "",
      appOk: false,
      appForm: emptyAppForm(),
      reportSubmitting: false,
      reportMsg: "",
      reportOk: false,
      reportForm: {
        submit_target_id: 0,
        is_past_training: true,
        officer_name: "",
        designation: "",
        department_section: "",
        station: "",
        training_title: "",
        institution_organizer: "",
        venue: "",
        date_from: "",
        date_to: "",
        sponsoring_agency: "",
        training_type: "",
        objectives: "",
        summary_content: "",
        key_learnings: "",
        application_relevance: "",
        challenges: "",
        recommendations: "",
        attachments_note: "",
        attachment_invitation: false,
        attachment_certificate: false,
        attachment_materials: false,
        officer_signature: "",
        officer_declaration_date: "",
        supervisor_comments: "",
        supervisor_name: "",
        supervisor_designation: "",
        supervisor_signature: "",
        supervisor_date: "",
        hr_received_by: "",
        hr_received_date: "",
        hr_remarks: "",
        hr_action_taken: ""
      }
    };
  },
  computed: {
    verifiedCount() {
      return this.certifications.filter(c => (c.verificationStatus || "").toLowerCase() === "verified").length;
    },
    formPdfUrl() {
      const base = process.env.BASE_URL || "/";
      const b = base.endsWith("/") ? base : base + "/";
      return `${b}docs/TRAINING_REPORT_FORM.pdf`;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    openReportForm() {
      this.showReportForm = true;
      this.$nextTick(() => {
        const el = this.$refs.reportFormPanel;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    stageLabel(s) {
      return STAGE_LABELS[s] || s || "—";
    },
    async openApplicationForm() {
      this.showAppForm = true;
      this.appMsg = "";
      await this.loadBiodataPrefill();
      this.$nextTick(() => {
        const el = this.$refs.appFormPanel;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    formatDate(v) {
      if (!v) return "—";
      const s = String(v).slice(0, 10);
      if (!s || s.startsWith("0000")) return "—";
      const parts = s.split("-");
      if (parts.length === 3) return parts[1] + "/" + parts[2] + "/" + parts[0];
      return s;
    },
    async loadBiodataPrefill() {
      this.prefillLoading = true;
      this.hasPrefill = false;
      try {
        const courseKeep = {
          id: this.appForm.id,
          last_course_title: this.appForm.last_course_title,
          last_course_from: this.appForm.last_course_from,
          last_course_to: this.appForm.last_course_to,
          last_bond_period: this.appForm.last_bond_period,
          last_bond_amount: this.appForm.last_bond_amount,
          last_bond_expired: this.appForm.last_bond_expired,
          course_title: this.appForm.course_title,
          course_from: this.appForm.course_from,
          course_to: this.appForm.course_to,
          mode_of_study: this.appForm.mode_of_study,
          venue: this.appForm.venue,
          sponsor: this.appForm.sponsor,
          total_cost: this.appForm.total_cost,
        };
        const fd = new FormData();
        fd.append("function", "taGetBiodataPrefill");
        const res = await execute(fd, authUrl);
        const emp = res.data?.data?.employee;
        this.appForm = emptyAppForm();
        Object.assign(this.appForm, courseKeep);
        if (emp && (emp.full_name || emp.emp_no || emp.employee_id)) {
          const personalKeys = [
            "full_name", "emp_no", "national_id", "designation", "job_group", "department_name",
            "section_name", "station", "sub_county", "address_text", "phone", "email",
            "date_of_birth", "gender", "disability", "date_first_appointment", "is_health_path"
          ];
          personalKeys.forEach((k) => {
            if (emp[k] != null && emp[k] !== "") this.appForm[k] = emp[k];
          });
          this.appForm.applicant_signature = emp.full_name || this.appForm.applicant_signature;
          this.hasPrefill = true;
        } else {
          this.hasPrefill = false;
        }
      } catch (_) {
        this.hasPrefill = false;
      } finally {
        this.prefillLoading = false;
      }
    },
    async loadMyApplications() {
      try {
        const fd = new FormData();
        fd.append("function", "taListMyApplications");
        const res = await execute(fd, authUrl);
        this.applications = res.data?.success && Array.isArray(res.data.data?.applications)
          ? res.data.data.applications
          : [];
        this.notices = res.data?.success && Array.isArray(res.data.data?.notices)
          ? res.data.data.notices
          : [];
      } catch (_) {
        this.applications = [];
        this.notices = [];
      }
    },
    async submitApplication(submit) {
      if (!this.appForm.course_title || !String(this.appForm.course_title).trim()) {
        this.appOk = false;
        this.appMsg = "Title of course requested is required.";
        return;
      }
      this.appSubmitting = true;
      this.appMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", submit ? "taSubmitApplication" : "taSaveApplication");
        if (this.appForm.id) fd.append("id", this.appForm.id);
        const skipOfficial = [
          "is_health_path", "supervisor_justification", "supervisor_name", "supervisor_designation",
          "supervisor_signed_at", "authorized_officer_justification", "authorized_officer_name",
          "authorized_officer_designation", "authorized_officer_signed_at"
        ];
        Object.keys(this.appForm).forEach((k) => {
          if (k === "id" || skipOfficial.includes(k)) return;
          const v = this.appForm[k];
          fd.append(k, v == null ? "" : String(v));
        });
        const res = await execute(fd, authUrl);
        this.appOk = !!res.data?.success;
        this.appMsg = res.data?.message || (this.appOk ? "Saved." : "Failed");
        if (this.appOk) {
          if (res.data.data?.id) this.appForm.id = res.data.data.id;
          if (submit) {
            this.showAppForm = false;
            this.appForm = emptyAppForm();
          }
          await this.loadMyApplications();
        }
      } catch (e) {
        this.appOk = false;
        this.appMsg = e?.message || "Could not save application.";
      } finally {
        this.appSubmitting = false;
      }
    },
    applyProfileToReportForm(userInfo) {
      if (userInfo.names && !this.reportForm.officer_name) {
        this.reportForm.officer_name = userInfo.names;
      }
    },
    gotTo(route) {
      const path = route.startsWith("/") ? route : "/" + route;
      this.$router.push(path);
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s === "verified") return "hr-badge-success";
      if (s === "pending") return "hr-badge-warning";
      return "hr-badge-secondary";
    },
    async loadData() {
      this.loading = true;
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
      const empNo = userInfo.idNo || sessionStorage.getItem("sessionIdNo") || "";

      try {
        if (empNo) {
          const certData = new FormData();
          certData.append("function", "getEmployeeCertifications");
          certData.append("empNo", empNo);
          const certRes = await execute(certData, authUrl);
          const certPayload = certRes?.data?.data;
          this.certifications = Array.isArray(certPayload?.certifications) ? certPayload.certifications : [];
        } else {
          this.certifications = [];
        }

        await this.loadScheduledTrainings();

        this.applyProfileToReportForm(userInfo);
        await this.loadSubmitTargets();
        await this.loadMyReports();
        await this.loadMyApplications();
      } catch (_) {
        this.certifications = [];
        this.scheduledTrainings = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSubmitTargets() {
      this.targetsError = "";
      try {
        const fd = new FormData();
        fd.append("function", "getTrainingReportSubmitTargets");
        const res = await execute(fd, authUrl);
        if (res.data?.success && res.data.data?.targets) {
          this.submitTargets = res.data.data.targets;
          if (!this.reportForm.submit_target_id && this.submitTargets.length) {
            this.reportForm.submit_target_id = 0;
          }
        } else {
          this.submitTargets = [];
          this.targetsError = res.data?.message || "Could not load submit-to options. Run DB script training_report_form_module.sql if needed.";
        }
      } catch (e) {
        this.submitTargets = [];
        this.targetsError = e?.message || "Could not load submit-to options.";
      }
    },
    async loadScheduledTrainings() {
      try {
        const fd = new FormData();
        fd.append("function", "getMyTrainingSchedules");
        const res = await execute(fd, authUrl);
        this.scheduledTrainings = res.data?.success && Array.isArray(res.data.data?.schedules)
          ? res.data.data.schedules
          : [];
      } catch (_) {
        this.scheduledTrainings = [];
      }
    },
    openScheduleDetail(s) {
      this.detailSchedule = { ...s };
      this.ackMsg = "";
    },
    async acknowledgeSchedule(s) {
      const id = s.schedule_id;
      this.ackSubmitting = id;
      this.ackMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "acknowledgeTrainingSchedule");
        fd.append("scheduleID", id);
        const res = await execute(fd, authUrl);
        this.ackOk = !!res.data?.success;
        this.ackMsg = res.data?.message || (this.ackOk ? "Confirmed." : "Could not confirm.");
        if (this.ackOk) {
          await this.loadScheduledTrainings();
          if (this.detailSchedule && this.detailSchedule.schedule_id === id) {
            const updated = this.scheduledTrainings.find(x => x.schedule_id === id);
            if (updated) this.detailSchedule = { ...updated };
          }
        }
      } catch (e) {
        this.ackOk = false;
        this.ackMsg = e?.message || "Could not confirm attendance.";
      } finally {
        this.ackSubmitting = 0;
      }
    },
    async loadMyReports() {
      try {
        const fd = new FormData();
        fd.append("function", "listMyTrainingReports");
        const res = await execute(fd, authUrl);
        this.myReports = res.data?.success && Array.isArray(res.data.data?.reports) ? res.data.data.reports : [];
      } catch (_) {
        this.myReports = [];
      }
    },
    async submitTrainingReport() {
      if (!this.reportForm.training_title.trim()) {
        this.reportOk = false;
        this.reportMsg = "Training title is required.";
        return;
      }
      if (!this.reportForm.submit_target_id) {
        this.reportOk = false;
        this.reportMsg = "Select where the report is to be submitted.";
        return;
      }
      this.reportSubmitting = true;
      this.reportMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "submitMyTrainingReport");
        fd.append("is_past_training", this.reportForm.is_past_training ? "1" : "0");
        Object.keys(this.reportForm).forEach((k) => {
          if (k === "is_past_training") return;
          const v = this.reportForm[k];
          if (typeof v === "boolean") {
            fd.append(k, v ? "1" : "0");
          } else {
            fd.append(k, v == null || v === "" ? "" : String(v));
          }
        });
        const inv = this.$refs.docInvitation?.files?.[0];
        const cert = this.$refs.docCertificate?.files?.[0];
        const mat = this.$refs.docMaterials?.files?.[0];
        if (inv) fd.append("training_doc_invitation", inv);
        if (cert) fd.append("training_doc_certificate", cert);
        if (mat) fd.append("training_doc_materials", mat);
        const res = await execute(fd, authUrl);
        this.reportOk = !!res.data?.success;
        this.reportMsg = res.data?.message || (this.reportOk ? "Submitted." : "Submit failed.");
        if (this.reportOk) {
          this.showReportForm = false;
          const keep = {
            officer_name: this.reportForm.officer_name,
            designation: this.reportForm.designation,
            department_section: this.reportForm.department_section,
            station: this.reportForm.station
          };
          this.reportForm = {
            submit_target_id: 0,
            is_past_training: true,
            officer_name: keep.officer_name,
            designation: keep.designation,
            department_section: keep.department_section,
            station: keep.station,
            training_title: "",
            institution_organizer: "",
            venue: "",
            date_from: "",
            date_to: "",
            sponsoring_agency: "",
            training_type: "",
            objectives: "",
            summary_content: "",
            key_learnings: "",
            application_relevance: "",
            challenges: "",
            recommendations: "",
            attachments_note: "",
            attachment_invitation: false,
            attachment_certificate: false,
            attachment_materials: false,
            officer_signature: "",
            officer_declaration_date: "",
            supervisor_comments: "",
            supervisor_name: "",
            supervisor_designation: "",
            supervisor_signature: "",
            supervisor_date: "",
            hr_received_by: "",
            hr_received_date: "",
            hr_remarks: "",
            hr_action_taken: ""
          };
          if (this.$refs.docInvitation) this.$refs.docInvitation.value = "";
          if (this.$refs.docCertificate) this.$refs.docCertificate.value = "";
          if (this.$refs.docMaterials) this.$refs.docMaterials.value = "";
          await this.loadMyReports();
        }
      } catch (e) {
        this.reportOk = false;
        this.reportMsg = e?.message || "Submit failed.";
      } finally {
        this.reportSubmitting = false;
      }
    }
  }
};
</script>


<style scoped>
.mt-page { max-width: 1100px; }
.metrics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; margin-bottom: 20px; }
.metric-card { display: flex; align-items: center; gap: 14px; padding: 18px; border-radius: 14px; background: #fff; border: 1px solid #e2e8f0; box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.metric-icon { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; color: #fff; }
.metric-card.gradient-1 .metric-icon { background: #0f766e; }
.metric-card.gradient-2 .metric-icon { background: #0284c7; }
.metric-card.gradient-3 .metric-icon { background: #7c3aed; }
.metric-card.gradient-4 .metric-icon { background: #059669; }
.metric-label { margin: 0; font-size: 0.8rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
.metric-value { margin: 4px 0 0; font-size: 1.6rem; font-weight: 700; color: #0f172a; }
.mt-notice-strip { background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; padding: 12px 16px; margin-bottom: 16px; }
.mt-notice-head { font-weight: 700; color: #92400e; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.mt-notice-strip ul { margin: 0; padding: 0; list-style: none; }
.mt-notice-strip li { display: grid; grid-template-columns: 140px 1fr; gap: 10px; font-size: 0.9rem; padding: 4px 0; border-top: 1px dashed #fde68a; }
.mt-notice-strip time { color: #a16207; font-size: 0.8rem; }
.mt-actions-bar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
.mt-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; font-weight: 600; cursor: pointer; font-size: 0.92rem; }
.mt-btn:hover { background: #f8fafc; }
.mt-btn.primary { background: #0f766e; border-color: #0f766e; color: #fff; }
.mt-btn.primary:hover { filter: brightness(1.05); }
.mt-btn.ghost { background: transparent; }
.mt-btn.sm { padding: 6px 12px; font-size: 0.85rem; }
.mt-btn:disabled { opacity: .65; cursor: not-allowed; }
.mt-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px 20px; margin-bottom: 18px; box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.mt-panel-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
.mt-panel-head h2, .mt-panel .hr-chart-title { margin: 0; font-size: 1.15rem; color: #0f172a; }
.mt-panel-head p { margin: 4px 0 0; color: #64748b; font-size: 0.9rem; }
.mt-section { margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }
.mt-section.accent { background: linear-gradient(180deg, #f0fdfa 0%, #fff 48%); border: 1px solid #99f6e4; border-radius: 12px; padding: 16px; border-bottom: 1px solid #99f6e4; }
.mt-section h3 { margin: 0 0 12px; font-size: 1rem; color: #0f766e; display: flex; align-items: center; gap: 8px; }
.mt-pill { background: #ccfbf1; color: #0f766e; font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; font-weight: 700; text-transform: uppercase; }
.mt-bio-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
@media (max-width: 900px) { .mt-bio-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 560px) {
  .mt-bio-grid { grid-template-columns: 1fr; }
  .mt-fields { grid-template-columns: 1fr !important; }
  .mt-fields .span2, .mt-bio.span2 { grid-column: span 1; }
}
.mt-bio { background: #f8fafc; border-radius: 8px; padding: 10px 12px; border: 1px solid #e2e8f0; }
.mt-bio.span2 { grid-column: span 2; }
.mt-bio label { display: block; font-size: 0.72rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
.mt-bio div { color: #0f172a; font-weight: 600; font-size: 0.95rem; word-break: break-word; }
.mt-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
.mt-fields .span2 { grid-column: span 2; }
.mt-fields label { display: flex; flex-direction: column; gap: 5px; font-size: 0.82rem; font-weight: 600; color: #334155; }
.mt-fields label em { color: #dc2626; font-style: normal; }
.mt-fields input, .mt-fields select, .tr-input, .tr-textarea {
  width: 100%; padding: 9px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.92rem; font-weight: 400; background: #fff;
}
.mt-fields input:focus, .mt-fields select:focus { outline: none; border-color: #0f766e; box-shadow: 0 0 0 3px rgba(15,118,110,.12); }
.mt-form-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; margin-top: 8px; }
.mt-msg { font-weight: 600; margin: 8px 0; }
.mt-msg.ok { color: #059669; }
.mt-msg.err { color: #dc2626; }
.mt-loading, .mt-empty { text-align: center; padding: 28px 12px; color: #64748b; }
.mt-alert.warn { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; padding: 12px; border-radius: 10px; }
.mt-table-wrap, .hr-table-wrapper { overflow-x: auto; }
.mt-table, .hr-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.mt-table th, .mt-table td, .hr-table th, .hr-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; text-align: left; vertical-align: top; }
.mt-table th, .hr-table th { font-size: 0.72rem; text-transform: uppercase; color: #64748b; letter-spacing: .04em; }
.mt-stage, .hr-badge-info { display: inline-block; background: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 999px; font-size: 0.78rem; font-weight: 600; }
.mt-status { font-weight: 600; text-transform: capitalize; font-size: 0.85rem; }
.mt-status.submitted, .mt-status.in_review, .mt-status.awaiting_bonding { color: #b45309; }
.mt-status.approved, .mt-status.completed, .hr-badge-success { color: #059669; }
.mt-status.rejected { color: #dc2626; }
.muted, .hr-text-muted { color: #64748b; }
.small, .hr-small { font-size: 0.85rem; }
.tr-form { max-width: 960px; }
.tr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
@media (max-width: 768px) { .tr-grid { grid-template-columns: 1fr; } .tr-span-2 { grid-column: span 1; } }
.tr-span-2 { grid-column: span 2; }
.tr-field span { display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 4px; color: #334155; }
.tr-field .req { color: #dc2626; }
.tr-check-row span { display: flex; align-items: flex-start; gap: 10px; font-weight: 500; font-size: 0.9rem; color: #334155; }
.tr-section-title { font-weight: 700; font-size: 0.95rem; color: #0f172a; border-top: 1px dashed #cbd5e1; padding-top: 10px; margin-top: 6px; grid-column: 1 / -1; }
.tr-textarea { resize: vertical; min-height: 64px; }
.hr-link-pdf { color: #0f766e; font-weight: 600; text-decoration: none; }
.hr-text-success { color: #059669; }
.hr-text-danger { color: #dc2626; }
.hr-alert { padding: 10px 12px; border-radius: 8px; font-size: 0.9rem; }
.hr-alert-warning { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }
.hr-mt-2 { margin-top: 8px; }
.hr-mt-4 { margin-top: 16px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-chart-header { margin-bottom: 12px; }
.dashboard-content { margin-left: 280px; padding: 20px; transition: margin-left 0.3s ease; }
.employee-dashboard.nav-collapsed .dashboard-content { margin-left: 70px; }
@media (max-width: 768px) { .dashboard-content { margin-left: 0; } }
.modern-page-header { margin-bottom: 20px; }
.header-content { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.dashboard-title { margin: 0; font-size: 1.75rem; color: #0f172a; }
.dashboard-subtitle { margin: 6px 0 0; color: #64748b; }
.training-list { display: flex; flex-direction: column; gap: 12px; }
.training-item { display: flex; align-items: flex-start; gap: 16px; padding: 14px 16px; background: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0; }
.training-item-icon { width: 44px; height: 44px; border-radius: 10px; background: #0f766e; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
.training-item-body { flex: 1; }
.training-item-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.spin, .bx-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.45); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px; }
.hr-modal { background: #fff; border-radius: 14px; max-width: 560px; width: 100%; max-height: 90vh; overflow: auto; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-header h3 { margin: 0; font-size: 1.05rem; }
.hr-modal-close { border: 0; background: none; font-size: 1.5rem; cursor: pointer; line-height: 1; }
.detail-body { padding: 16px 18px; }
.detail-body p { margin: 0 0 8px; font-size: 0.92rem; }
.hr-d-block { display: block; }
.hr-ml-2 { margin-left: 8px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-text-center { text-align: center; }
.hr-py-4 { padding-top: 16px; padding-bottom: 16px; }
.refresh-btn { padding: 8px 14px; border-radius: 8px; background: #0f766e; color: #fff; border: none; cursor: pointer; font-weight: 600; }
.refresh-btn.sm { padding: 6px 12px; font-size: 0.85rem; background: #fff; color: #334155; border: 1px solid #cbd5e1; }
.refresh-btn.sm.primary, .refresh-btn.primary { background: #0f766e; color: #fff; border-color: #0f766e; }
.hr-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.78rem; font-weight: 600; }
.hr-badge-success { background: #d1fae5; color: #065f46; }
.hr-badge-warning { background: #fef3c7; color: #92400e; }
.hr-badge-secondary { background: #f1f5f9; color: #475569; }
</style>
